<template>
  <div class="waiting-room-root">
    <!-- Left: Member List -->
    <div class="member-list-panel">
      <div class="panel-header">Team Members</div>
      <div class="member-list">
        <div
          v-for="member in displayMembers"
          :key="member.student_id"
          class="member-item"
          :class="{
            'member-online': member.is_online,
            'member-in-exam': member.is_in_exam
          }"
        >
          <div class="member-avatar">
            <img
              v-if="member.avatar_url"
              :src="member.avatar_url"
              alt="avatar"
            />
            <div v-else class="avatar-placeholder">
              {{ member.student_name ? member.student_name.charAt(0).toUpperCase() : '?' }}
            </div>
            <span
              class="online-indicator"
              :class="member.is_online ? 'online' : 'offline'"
            ></span>
          </div>
          <div class="member-info">
            <div class="member-name">{{ member.student_name }}</div>
            <div class="member-status">
              <span v-if="member.is_in_exam" class="status-in-exam">
                In Exam
              </span>
              <span v-else-if="member.is_online" class="status-online">
                Online
              </span>
              <span v-else class="status-offline">
                Offline
              </span>
            </div>
          </div>
          <div v-if="member.entry_count > 0" class="entry-count">
            {{ member.entry_count }}/2
          </div>
        </div>
      </div>
    </div>

    <!-- Center: Team Chat -->
    <div class="chat-panel">
      <div class="panel-header">Team Chat</div>
      <div class="chat-warning">
        Note: Team chat is only for team strategy discussions—question-related talk is strictly forbidden. All chat content is audited; violations will result in team penalties.
      </div>
      <div class="chat-messages" ref="chatMessages">
        <div
          v-for="(message, index) in chatMessages"
          :key="index"
          class="chat-message"
          :class="{ 'my-message': message.sender_id === currentUserId }"
        >
          <div class="message-sender">{{ message.sender_name }}</div>
          <div class="message-content">{{ message.content }}</div>
          <div class="message-time">{{ formatTime(message.send_time) }}</div>
        </div>
        <div v-if="chatMessages.length === 0" class="no-messages">
          No messages yet. Start the conversation!
        </div>
      </div>
      <div class="chat-input-area">
        <el-input
          v-model="newMessage"
          placeholder="Type your message..."
          maxlength="500"
          @keyup.enter.native="sendMessage"
        ></el-input>
        <el-button type="primary" @click="sendMessage" :loading="sendingMessage">
          Send
        </el-button>
      </div>
    </div>

    <!-- Right: Actions Panel -->
    <div class="actions-panel">
      <div class="panel-header">Exam Actions</div>
      <div class="exam-info">
        <div v-if="projectDisplayName" class="info-item">
          <span class="info-label">Project:</span>
          <span class="info-value">{{ projectDisplayName }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Exam:</span>
          <span class="info-value">{{ examName }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Your Entry Count:</span>
          <span class="info-value">{{ currentUserEntryCount }}/2</span>
        </div>
        <div v-if="wsExamTaker" class="info-item">
          <span class="info-label">Current Exam Taker:</span>
          <span class="info-value">{{ wsExamTaker.student_name }}</span>
        </div>
      </div>
      <el-button
        type="danger"
        size="large"
        class="enter-exam-btn"
        @click="handleEnterExam"
      >
        Enter Exam
      </el-button>
      <el-button
        size="large"
        icon="el-icon-chat-dot-square"
        class="messages-btn"
        @click="quickMessageVisible = true"
      >
        Messages
      </el-button>
      <el-button
        size="large"
        class="back-to-list-btn"
        @click="goBackToList"
      >
        Back to Exam List
      </el-button>
    </div>

    <quick-message-drawer
      :visible.sync="quickMessageVisible"
      :student-id="currentUserId"
      :project-code="projectCode"
      :template-name="templateId"
      :snapshot="waitingRoomSnapshot"
    />

    <!-- Confirm Enter Exam Dialog -->
    <el-dialog
      title="Confirm Entry"
      :visible.sync="confirmEnterDialogVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <p v-if="wsExamTaker" style="word-break: normal; overflow-wrap: break-word;">
        Only one member can access the quiz interface at a time. Confirming your entry will kick out the current quiz-taker <strong style="color: #E6A23C;">{{ wsExamTaker.student_name }}</strong>. Additionally, each member is allowed to access the interface a maximum of two times. Do you confirm to take over?
      </p>
      <p v-else style="word-break: normal; overflow-wrap: break-word;">
        You are about to enter the exam. Each member is allowed to access the interface a maximum of two times. Do you confirm to enter?
      </p>
      <span slot="footer" class="dialog-footer">
        <el-button @click="confirmEnterDialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="confirmEnterExam" :loading="enteringExam">
          Confirm
        </el-button>
      </span>
    </el-dialog>

    <!-- Kicked Out Dialog -->
    <el-dialog
      title="Notice"
      :visible.sync="kickedOutDialogVisible"
      width="400px"
      :close-on-click-modal="false"
      :show-close="false"
    >
      <p><strong>{{ kickedByName }}</strong> has taken over the exam.</p>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="acknowledgeKickOut">OK</el-button>
      </span>
    </el-dialog>

    <!-- Max Entry Dialog -->
    <el-dialog
      title="Entry Limit Reached"
      :visible.sync="maxEntryDialogVisible"
      width="400px"
      :close-on-click-modal="false"
    >
      <p>You have used up your 2 chances to join the relay.</p>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="maxEntryDialogVisible = false">OK</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getUserId, resolveAndSetExamProjectCode, getUserName, getProjectEname, getProjectName } from "../../utils/store";
import {
  getRelayTeamMembers,
  getRelayTeamChat,
  sendRelayTeamMessage,
  checkRelayEntryCount,
  enterRelayExam,
  getRelayExamStatus
} from "../../api/index";
import QuickMessageDrawer from "../Support/QuickMessageDrawer.vue";

export default {
  name: "WaitingRoom",
  components: {
    QuickMessageDrawer
  },
  data() {
    return {
      currentUserId: getUserId(),
      currentUserName: getUserName(),
      projectCode: resolveAndSetExamProjectCode(this.$route.query.project_code),
      projectDisplayName: getProjectEname() || getProjectName() || '',
      templateId: this.$route.query.template_id,
      examName: this.$route.query.exam_name || "Relay Exam",

      teamMembers: [],
      onlineMembers: [],
      chatMessages: [],
      currentUserEntryCount: 0,
      currentExamTaker: null,

      confirmEnterDialogVisible: false,
      kickedOutDialogVisible: false,
      maxEntryDialogVisible: false,

      newMessage: "",
      sendingMessage: false,
      enteringExam: false,
      kickedByName: "",
      quickMessageVisible: false,

      ws: null,
      refreshTimer: null,
      heartbeatTimer: null
    };
  },
  computed: {
    waitingRoomSnapshot() {
      return {
        page: "waiting_room",
        template_id: this.templateId,
        exam_name: this.examName,
        team_ref: `relay_${this.templateId}_${this.projectCode}`
      };
    },
    displayMembers() {
      return this.teamMembers.map(m => {
        const wsEntry = this.onlineMembers.find(e => e.id === m.student_id);
        return {
          ...m,
          is_online: !!wsEntry,
          is_in_exam: wsEntry ? wsEntry.status === 'exam' : false
        };
      });
    },
    wsExamTaker() {
      const examEntry = this.onlineMembers.find(e => e.status === 'exam');
      if (!examEntry) return null;
      const member = this.teamMembers.find(m => m.student_id === examEntry.id);
      return member ? { student_name: member.student_name, student_id: examEntry.id } : { student_name: examEntry.id, student_id: examEntry.id };
    }
  },
  mounted() {
    this.initData();
    this.connectWebSocket();
    this.startRefreshTimer();
  },
  beforeDestroy() {
    this.disconnectWebSocket();
    this.stopRefreshTimer();
  },
  methods: {
    async initData() {
      await Promise.all([
        this.fetchTeamMembers(),
        this.fetchChatMessages(),
        this.fetchExamStatus()
      ]);
    },

    async fetchTeamMembers() {
      try {
        const res = await getRelayTeamMembers(
          this.currentUserId,
          this.templateId,
          this.projectCode
        );
        if (res.data.code === 0) {
          this.teamMembers = res.data.data.members || [];
          const currentMember = this.teamMembers.find(
            m => m.student_id === this.currentUserId
          );
          if (currentMember) {
            this.currentUserEntryCount = currentMember.entry_count || 0;
          }
        }
      } catch (error) {
        console.error("Failed to fetch team members:", error);
      }
    },

    async fetchChatMessages() {
      try {
        const res = await getRelayTeamChat(
          this.currentUserId,
          this.templateId,
          this.projectCode
        );
        if (res.data.code === 0) {
          this.chatMessages = res.data.data.messages || [];
          this.$nextTick(() => {
            this.scrollChatToBottom();
          });
        }
      } catch (error) {
        console.error("Failed to fetch chat messages:", error);
      }
    },

    async fetchExamStatus() {
      try {
        const res = await getRelayExamStatus(
          this.currentUserId,
          this.templateId,
          this.projectCode
        );
        if (res.data.code === 0) {
          this.currentExamTaker = res.data.data.current_exam_taker || null;
        }
      } catch (error) {
        console.error("Failed to fetch exam status:", error);
      }
    },

    connectWebSocket() {
      const wsUrl = "wss://team-chat.learningfirst.cn:9000";
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log("WebSocket connected");
        const groupId = `relay_${this.templateId}_${this.projectCode}`;
        this.ws.send(`user&${groupId}&${this.currentUserId}&waiting`);
        this.startHeartbeat();
        setTimeout(() => {
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send('relay_get_online');
          }
        }, 500);
      };

      this.ws.onmessage = (event) => {
        const data = event.data;
        if (data instanceof Blob) {
          data.text().then(text => this.handleWebSocketMessage(text));
        } else {
          this.handleWebSocketMessage(data);
        }
      };

      this.ws.onclose = () => {
        console.log("WebSocket disconnected");
        this.stopHeartbeat();
        // Attempt to reconnect after 3 seconds
        setTimeout(() => {
          if (this._isDestroyed) return;
          this.connectWebSocket();
        }, 3000);
      };

      this.ws.onerror = (error) => {
        console.error("WebSocket error:", error);
      };
    },

    disconnectWebSocket() {
      this._isDestroyed = true;
      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }
    },

    sendWsMessage(type, payload) {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(`${type}&${JSON.stringify(payload)}`);
      }
    },

    startHeartbeat() {
      this.heartbeatTimer = setInterval(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send("getGroupConnection");
        }
      }, 30000);
    },

    stopHeartbeat() {
      if (this.heartbeatTimer) {
        clearInterval(this.heartbeatTimer);
        this.heartbeatTimer = null;
      }
    },

    handleWebSocketMessage(data) {
      try {
        const str = typeof data === 'string' ? data : data.toString();
        if (str.indexOf("relay_online_list&") === 0) {
          this.onlineMembers = JSON.parse(str.substring("relay_online_list&".length));
        } else if (str.indexOf("relay_chat&") === 0) {
          const msgData = JSON.parse(str.substring("relay_chat&".length));
          this.chatMessages.push(msgData);
          this.$nextTick(() => {
            this.scrollChatToBottom();
          });
        } else if (str.indexOf("relay_member_update&") === 0) {
          try {
            const updateData = JSON.parse(str.substring("relay_member_update&".length));
            if (updateData.reason === 'connect' && updateData.user_id) {
              const existing = this.onlineMembers.find(e => e.id === updateData.user_id);
              if (existing) {
                existing.status = updateData.status || 'online';
              } else {
                this.onlineMembers.push({ id: updateData.user_id, status: updateData.status || 'online' });
              }
            } else if (updateData.reason === 'disconnect' && updateData.user_id) {
              this.onlineMembers = this.onlineMembers.filter(e => e.id !== updateData.user_id);
            }
          } catch(e) {
            this.fetchTeamMembers();
            this.fetchExamStatus();
          }
        } else if (str.indexOf("relay_exam_takeover&") === 0) {
          const takeoverData = JSON.parse(str.substring("relay_exam_takeover&".length));
          if (takeoverData.kicked_user_id === this.currentUserId) {
            this.kickedByName = takeoverData.new_taker_name;
            this.kickedOutDialogVisible = true;
          }
          this.fetchExamStatus();
          this.fetchTeamMembers();
        }
      } catch (error) {
        console.error("Failed to parse WebSocket message:", error);
      }
    },

    startRefreshTimer() {
      this.refreshTimer = setInterval(() => {
        this.fetchTeamMembers();
        this.fetchChatMessages();
        this.refreshOnlineList();
      }, 10000);
    },

    stopRefreshTimer() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },

    scrollChatToBottom() {
      const chatContainer = this.$refs.chatMessages;
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    },

    formatTime(timestamp) {
      if (!timestamp) return "";
      const date = new Date(timestamp * 1000);
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${hours}:${minutes}`;
    },

    async sendMessage() {
      if (!this.newMessage.trim()) {
        this.$message.warning("Please enter a message");
        return;
      }

      const messageContent = this.newMessage.trim();
      this.sendingMessage = true;
      try {
        const res = await sendRelayTeamMessage(
          this.currentUserId,
          this.currentUserName,
          this.templateId,
          this.projectCode,
          messageContent
        );
        if (res.data.code === 0) {
          const msgObj = {
            sender_id: this.currentUserId,
            sender_name: this.currentUserName,
            content: messageContent,
            send_time: Math.floor(Date.now() / 1000)
          };
          this.chatMessages.push(msgObj);
          this.sendWsMessage("relay_chat", msgObj);
          this.newMessage = "";
          this.$message.success("Message sent");
          this.$nextTick(() => {
            this.scrollChatToBottom();
          });
        } else {
          this.$message.error(res.data.msg || "Failed to send message");
        }
      } catch (error) {
        console.error("Failed to send message:", error);
        this.$message.error("Failed to send message");
      } finally {
        this.sendingMessage = false;
        this.fetchChatMessages()
      }
    },

    async handleEnterExam() {
      // First check entry count
      try {
        const res = await checkRelayEntryCount(
          this.currentUserId,
          this.templateId,
          this.projectCode
        );
        if (res.data.code === 0) {
          const entryCount = res.data.data.entry_count || 0;
          this.currentUserEntryCount = entryCount;

          if (entryCount >= 2) {
            this.maxEntryDialogVisible = true;
          } else {
            // Refresh exam status before showing confirm dialog
            await this.fetchExamStatus();
            this.confirmEnterDialogVisible = true;
          }
        } else {
          this.$message.error(res.data.msg || "Failed to check entry count");
        }
      } catch (error) {
        console.error("Failed to check entry count:", error);
        this.$message.error("Failed to check entry count");
      }
    },

    async confirmEnterExam() {
      this.enteringExam = true;
      try {
        const res = await enterRelayExam(
          this.currentUserId,
          this.currentUserName,
          this.templateId,
          this.projectCode
        );
        if (res.data.code === 0) {
          this.confirmEnterDialogVisible = false;
          if (res.data.data.kicked_user_id) {
            this.sendWsMessage("relay_exam_takeover", {
              kicked_user_id: res.data.data.kicked_user_id,
              kicked_user_name: res.data.data.kicked_user_name,
              new_taker_id: this.currentUserId,
              new_taker_name: this.currentUserName
            });
          }
          this.sendWsMessage("relay_member_update", {});
          setTimeout(() => {
            this.$router.replace({
              path: "/exam",
              query: {
                template_id: this.templateId,
                invigilateStatus: false,
                relay_mode: true
              }
            });
          }, 300);
        } else {
          this.$message.error(res.data.msg || "Failed to enter exam");
        }
      } catch (error) {
        console.error("Failed to enter exam:", error);
        this.$message.error("Failed to enter exam");
      } finally {
        this.enteringExam = false;
      }
    },

    acknowledgeKickOut() {
      this.kickedOutDialogVisible = false;
      // Stay in waiting room, refresh data
      this.fetchTeamMembers();
      this.fetchExamStatus();
    },

    refreshOnlineList() {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send('relay_get_online');
      }
    },

    goBackToList() {
      this.$router.push({ path: "/studentlist" });
    }
  }
};
</script>

<style lang="scss" scoped>
.waiting-room-root {
  display: flex;
  height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
  padding: 20px;
  box-sizing: border-box;
  gap: 20px;
}

.panel-header {
  background: #072d76;
  color: white;
  padding: 15px 20px;
  font-size: 18px;
  font-weight: bold;
}

// Member List Panel
.member-list-panel {
  width: 280px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.member-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
}

.member-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 8px;
  background: #f9f9f9;
  border-left: 4px solid transparent;
  transition: background 0.3s, border-color 0.3s;

  &:hover {
    background: #f0f0f0;
  }

  &.member-online {
    border-left-color: #4caf50;
  }

  &.member-in-exam {
    background: #fff3e0;
    border-left-color: #ff9800;
  }
}

.member-avatar {
  position: relative;
  width: 45px;
  height: 45px;
  margin-right: 12px;

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }

  .avatar-placeholder {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: #072d76;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: bold;
  }
}

.online-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid white;

  &.online {
    background: #4caf50;
  }

  &.offline {
    background: #9e9e9e;
  }
}

.member-info {
  flex: 1;
}

.member-name {
  font-weight: bold;
  color: #333;
  font-size: 14px;
}

.member-status {
  font-size: 12px;
  margin-top: 4px;

  .status-in-exam {
    color: #ff9800;
    font-weight: bold;
  }

  .status-online {
    color: #4caf50;
  }

  .status-offline {
    color: #9e9e9e;
  }
}

.entry-count {
  font-size: 12px;
  color: #666;
  background: #e0e0e0;
  padding: 2px 8px;
  border-radius: 10px;
}

// Chat Panel
.chat-panel {
  flex: 1;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-warning {
  background: #fff3cd;
  color: #856404;
  padding: 12px 20px;
  font-size: 13px;
  line-height: 1.5;
  border-bottom: 1px solid #ffeeba;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #fafafa;
}

.chat-message {
  max-width: 70%;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &.my-message {
    margin-left: auto;
    background: #e3f2fd;
  }
}

.message-sender {
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
  font-weight: bold;
}

.message-content {
  font-size: 14px;
  color: #333;
  line-height: 1.5;
  word-wrap: break-word;
}

.message-time {
  font-size: 11px;
  color: #999;
  margin-top: 6px;
  text-align: right;
}

.no-messages {
  text-align: center;
  color: #999;
  padding: 40px;
  font-size: 14px;
}

.chat-input-area {
  padding: 12px 16px;
  border-top: 1px solid #eee;
  display: flex;
  align-items: center;
  gap: 10px;

  .el-input {
    flex: 1;
  }

  .el-button {
    flex-shrink: 0;
  }
}

// Actions Panel
.actions-panel {
  width: 300px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.exam-info {
  padding: 20px;
  flex: 1;
}

.info-item {
  margin-bottom: 16px;

  .info-label {
    font-size: 13px;
    color: #666;
    display: block;
    margin-bottom: 4px;
  }

  .info-value {
    font-size: 16px;
    color: #333;
    font-weight: bold;
  }
}

.enter-exam-btn {
  margin: 20px 20px 10px 20px;
  height: 50px;
  font-size: 18px;
  font-weight: bold;
}

.messages-btn {
  margin: 0 20px 10px 20px;
  height: 42px;
  font-size: 14px;
  width: calc(100% - 40px);
  color: #072d76;
  border-color: #072d76;

  &:hover,
  &:focus {
    color: #11448d;
    border-color: #11448d;
    background: #f0f4ff;
  }
}

.back-to-list-btn {
  margin: 0 20px 20px 20px;
  height: 42px;
  font-size: 14px;
  width: calc(100% - 40px);
}
</style>
